import React, { forwardRef, useImperativeHandle, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Checkbox,
  FormGroup,
  FormControlLabel,
  Switch,
} from "@mui/material";
import { useTarget } from "../contexts/TargetContext";
import { useDialog } from "../contexts/DialogContext";

export type DeleteType = "target" | "night" | null;

export interface ConfirmDialogRef {
  showDeleteTarget: () => void;
  showDeleteNight: (night: string) => void;
}

export const ConfirmDialog = forwardRef<ConfirmDialogRef>((_, ref) => {
  const {
    confirmDialogOpen,
    setConfirmDialogOpen,
    deleteType,
    setDeleteType,
    nightToDelete,
    setNightToDelete,
    pendingDeleteTargetId,
    setPendingDeleteTargetId,
  } = useDialog();
  const {
    targets,
    handleDeleteNight,
    handleDeleteFramesByType,
    handleEdit,
    deleteFramesByType,
    setSelectedTarget,
    deleteTargetById,
  } = useTarget();

  // State for selective frame type deletion
  const [deleteSpecificTypes, setDeleteSpecificTypes] = useState(false);
  const [selectedTypes, setSelectedTypes] = useState<{
    LIGHT: boolean;
    DARK: boolean;
    FLAT: boolean;
    BIAS: boolean;
  }>({ LIGHT: true, DARK: true, FLAT: true, BIAS: true });

  // Reset state when dialog opens/closes
  React.useEffect(() => {
    if (confirmDialogOpen && deleteType === "night") {
      setDeleteSpecificTypes(false);
      setSelectedTypes({ LIGHT: true, DARK: true, FLAT: true, BIAS: true });
    }
  }, [confirmDialogOpen, deleteType]);

  useImperativeHandle(ref, () => ({
    showDeleteTarget: () => {
      console.log("showDeleteTarget called");
      setDeleteType("target");
      console.log("setDeleteType('target') called");
      setConfirmDialogOpen(true);
    },
    showDeleteNight: (night: string) => {
      console.log("showDeleteNight called with night:", night);
      setDeleteType("night");
      console.log("setDeleteType('night') called");
      setNightToDelete(night);
      setConfirmDialogOpen(true);
    },
  }));

  const handleClose = () => {
    console.log("handleClose called, current deleteType:", deleteType);
    setDeleteType(null);
    setNightToDelete(null);
    setConfirmDialogOpen(false);
    setPendingDeleteTargetId(null);
  };

  const handleTypeChange = (type: "LIGHT" | "DARK" | "FLAT" | "BIAS") => {
    setSelectedTypes((prev) => ({ ...prev, [type]: !prev[type] }));
  };

  const handleConfirm = async () => {
    console.log("handleConfirm called, current deleteType:", deleteType);
    if (deleteType === "target" && pendingDeleteTargetId) {
      await deleteTargetById(pendingDeleteTargetId);
      setPendingDeleteTargetId(null);
    } else if (deleteType === "night" && nightToDelete) {
      if (!deleteSpecificTypes || Object.values(selectedTypes).every(Boolean)) {
        // All types selected or not using specific types: delete whole night
        await handleDeleteNight(nightToDelete);
      } else {
        // Only delete selected frame types for this night
        const typesToDelete = (
          Object.entries(selectedTypes) as [
            "LIGHT" | "DARK" | "FLAT" | "BIAS",
            boolean
          ][]
        )
          .filter(([, v]) => v)
          .map(([k]) => k);
        if (typesToDelete.length === 1) {
          await handleDeleteFramesByType(typesToDelete[0], nightToDelete);
        } else if (pendingDeleteTargetId) {
          // Perform all deletions in-memory, then update once
          let updatedTarget = targets.find(
            (t: any) => t.id === pendingDeleteTargetId
          );
          if (updatedTarget) {
            for (const type of typesToDelete) {
              updatedTarget = deleteFramesByType(
                updatedTarget,
                type,
                nightToDelete
              );
            }
            await handleEdit(updatedTarget);
            setSelectedTarget(updatedTarget);
          }
        }
      }
    }
    handleClose();
  };

  const getTitle = () => {
    return "Confirm Delete";
  };

  const getMessage = () => {
    if (deleteType === "target") {
      return "Are you sure you want to delete this target? This cannot be undone.";
    }
    if (deleteType === "night" && nightToDelete) {
      if (!deleteSpecificTypes || Object.values(selectedTypes).every(Boolean)) {
        return `Are you sure you want to delete all data for night ${nightToDelete}? This cannot be undone.`;
      } else {
        const selected = Object.entries(selectedTypes)
          .filter(([, v]) => v)
          .map(([k]) => k)
          .join(", ");
        return `Delete the following frame types for night ${nightToDelete}: ${selected}. This cannot be undone.`;
      }
    }
    return "Are you sure you want to delete?";
  };

  return (
    <Dialog
      open={confirmDialogOpen}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
    >
      <DialogTitle>{getTitle()}</DialogTitle>
      <DialogContent>
        <Typography sx={{ mb: 2 }}>{getMessage()}</Typography>
        {deleteType === "night" && nightToDelete && (
          <>
            <FormControlLabel
              control={
                <Switch
                  checked={deleteSpecificTypes}
                  onChange={(_, checked) => setDeleteSpecificTypes(checked)}
                  color="primary"
                />
              }
              label="Delete only specific frame types"
              sx={{ mb: 1 }}
            />
            {deleteSpecificTypes && (
              <FormGroup row>
                {(["LIGHT", "DARK", "FLAT", "BIAS"] as const).map((type) => (
                  <FormControlLabel
                    key={type}
                    control={
                      <Checkbox
                        checked={selectedTypes[type]}
                        onChange={() => handleTypeChange(type)}
                        color="primary"
                      />
                    }
                    label={type}
                  />
                ))}
              </FormGroup>
            )}
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Cancel</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={
            deleteSpecificTypes && !Object.values(selectedTypes).some(Boolean)
          }
        >
          Delete
        </Button>
      </DialogActions>
    </Dialog>
  );
});
